/**
 * The plan's scale corrected to one length or one area known for real, as the app's «Изменить масштаб плана»
 * (`BlueprintScale`, `LevelScaler.Scale`): a wall's length typed on its label gives the factor typed ÷ shown, a room's
 * area typed on its label √(typed ÷ shown), and the floor — or every floor — is scaled by it about the middle of the
 * floor in view. What the app scales is scaled here: the walls' ends and thickness, a door's or window's width and
 * its place along its wall, where the furniture stands; what it keeps is kept: the heights, the sills, the furniture's
 * sizes. A piece on a wall keeps its gap to the wall's face. A recognized plan whose scale came out wrong is put right
 * this way (the app's hint after a recognition, «Проверьте масштаб»).
 */
import type { Item, Level, Scene, Vec2, Wall } from '../core/hp/types';
import { t } from '../i18n/I18n';

/** Factors the app accepts (`ChangeFPScale`: 0.01–10 000), and a typed value it ignores as no change (under 10 cm). */
export const PLAN_SCALE_LIMITS = { min: .01, max: 10000 } as const;

/** The middle of what is drawn on a floor: its walls, else its rooms, else the origin. */
export function planMiddle(level: Pick<Level, 'walls' | 'rooms'>): Vec2 {
  const points = level.walls.length ? level.walls.flatMap(wall => [wall.start, wall.end]) : level.rooms.flatMap(room => room.polygon);
  if (!points.length) return { x: 0, z: 0 };
  const xs = points.map(point => point.x), zs = points.map(point => point.z);
  return { x: (Math.min(...xs) + Math.max(...xs)) / 2, z: (Math.min(...zs) + Math.max(...zs)) / 2 };
}

function scaledWall(wall: Wall, factor: number, at: (point: Vec2) => Vec2): Wall {
  return { ...wall, start: at(wall.start), end: at(wall.end), center: at(wall.center), length: wall.length * factor, thickness: wall.thickness * factor };
}

/** A piece on a wall: its place along the wall scales, its gap to the wall's face stays. */
function scaledWallItem(item: Item, before: Wall, after: Wall, factor: number): Item {
  const local = { x: item.position.x - before.center.x, z: item.position.z - before.center.z };
  const along = local.x * before.dir.x + local.z * before.dir.z, across = local.x * before.normalIn.x + local.z * before.normalIn.z;
  const side = Math.sign(across) || 1, gap = Math.abs(across) - before.thickness / 2;
  const normal = side * (after.thickness / 2 + gap);
  return { ...item, position: { x: after.center.x + after.dir.x * along * factor + after.normalIn.x * normal, z: after.center.z + after.dir.z * along * factor + after.normalIn.z * normal } };
}

export function scaleLevel(level: Level, factor: number, middle: Vec2): Level {
  const at = (point: Vec2): Vec2 => ({ x: middle.x + (point.x - middle.x) * factor, z: middle.z + (point.z - middle.z) * factor });
  const walls = level.walls.map(wall => scaledWall(wall, factor, at));
  const wallOf = new Map(walls.map(wall => [wall.id, wall]));
  return {
    ...level,
    walls,
    outline: level.outline.map(at),
    rooms: level.rooms.map(room => ({ ...room, polygon: room.polygon.map(at), area: room.area * factor * factor })),
    openings: level.openings.map(opening => ({ ...opening, offset: opening.offset * factor, width: opening.width * factor })),
    items: level.items.map(item => {
      const before = item.wallId ? level.walls.find(wall => wall.id === item.wallId) : undefined, after = item.wallId ? wallOf.get(item.wallId) : undefined;
      return before && after ? scaledWallItem(item, before, after, factor) : { ...item, position: at(item.position) };
    }),
  };
}

/**
 * The factor a typed value asks for, or why it asks for none: a length against the one shown, an area against the one
 * shown (its square root), within the app's limits and more than 10 cm from what is shown (`ChangeDimension`).
 */
export function scaleFactor(kind: 'length' | 'area', typed: number, shown: number): { factor: number } | { error: string } | null {
  if (!Number.isFinite(typed) || typed < .1) return { error: kind === 'length' ? t('#PlanScaleEnterLength') : t('#PlanScaleEnterArea') };
  if (!(shown > 0)) return { error: t('#PlanScaleSizeUnusable') };
  if (Math.abs(typed - shown) < .1) return null;
  const factor = kind === 'length' ? typed / shown : Math.sqrt(typed / shown);
  if (factor < PLAN_SCALE_LIMITS.min || factor > PLAN_SCALE_LIMITS.max) return { error: t('#PlanScaleTooFar') };
  return { factor };
}

/** The floor in view scaled, or every floor about the same middle, as one change of the scene. */
export function scalePlan(scene: Scene, levelIndex: number, factor: number, everyFloor: boolean): Scene {
  const middle = planMiddle(scene.levels[levelIndex] ?? { walls: [], rooms: [] });
  return { ...scene, levels: scene.levels.map((level, index) => everyFloor || index === levelIndex ? scaleLevel(level, factor, middle) : level) };
}
